import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { Observable } from 'rxjs'

import { NetworkStateContext } from '@/modules/network/context.ts'
import { ApplicationNetworkState } from '@/system/rxdb/replication'

export const useNetworkState = () => {
  const networkState = useContext(NetworkStateContext)

  const observeStatusState = useCallback(() => {
    return networkState?.get$('status') as Observable<
      ApplicationNetworkState['status'] | undefined
    >
  }, [networkState])

  const observeLastErrorState = useCallback(() => {
    return networkState?.get$('lastError') as Observable<
      ApplicationNetworkState['lastError'] | undefined
    >
  }, [networkState])

  return useMemo(
    () => ({ observeStatusState, observeLastErrorState, networkState }),
    [networkState, observeLastErrorState, observeStatusState]
  )
}

export const useNavigatorOnlineState = () => {
  const [wasOffline, setWasOffline] = useState(false)
  const [online, setOnline] = useState(window.navigator.onLine)

  useEffect(() => {
    const online = () => {
      setWasOffline(true)
      setOnline(true)
    }

    const offline = () => {
      setOnline(false)
    }

    window.addEventListener('online', online)
    window.addEventListener('offline', offline)

    return () => {
      window.removeEventListener('online', online)
      window.removeEventListener('offline', offline)
    }
  }, [])

  return useMemo(
    () => ({
      wasOffline,
      online,
      resetWasOffline: () => setWasOffline(false),
    }),
    [wasOffline, online]
  )
}
